import { z } from 'zod';
import {
  AwardLetterWording,
  LetterMix,
  LetterWordingText,
  OperationalAwardTypeCode,
  StageId,
} from './awards.js';
import { Cents, PlainDate } from './common.js';
import { ScanStatus } from './documents.js';

/**
 * Award letters (PRD §11, SA3): Word or PDF templates kept by the Stock Admin team, one letter per
 * person generated from the award's saved facts, previewed, then released to the letter holder.
 */

export const LetterLibraryType = z.enum(['SDB', 'EDB', 'PAA', 'TAA', 'SPOT', 'SBIR', 'CRAD']);
export type LetterLibraryType = z.infer<typeof LetterLibraryType>;
export const LetterVariant = z.enum(['any', 'cash_only', 'options_only', 'cash_and_options']);
export type LetterVariant = z.infer<typeof LetterVariant>;
export const LetterLayout = z.enum(['standard', 'signed_letter']);
export const LetterSignerSource = z.enum(['executive', 'sector_lead', 'ceo', 'person']);

const PersonRef = z.object({ id: z.uuid(), name: z.string() });
const Timestamp = z.iso.datetime({ offset: true });

export const LetterTemplateFormat = z.enum(['docx', 'pdf']);
export type LetterTemplateFormat = z.infer<typeof LetterTemplateFormat>;

/** Who signs: a principal, or the CEO (Executive Discretionary letters always carry the CEO's). */
export const LetterSignerRole = z.enum(['principal', 'ceo', 'president']);
export type LetterSignerRole = z.infer<typeof LetterSignerRole>;

/**
 * `checking`: waiting for the virus check. `ready`: can be used. `default`: used unless the Stock
 * Admin picks another. `retired`: kept for letters already made. `blocked`: the virus check found
 * a threat or couldn't check the file; it is never used.
 */
export const LetterTemplateStatus = z.enum([
  'checking',
  'ready',
  'default',
  'retired',
  'blocked',
  'draft',
  'approved',
]);
export type LetterTemplateStatus = z.infer<typeof LetterTemplateStatus>;

/**
 * Something to know about a template that doesn't stop it being used. `options_on_cash_only`: a
 * cash-only letter from it shows its option rows empty (QA-044); a Word template can wrap them in
 * `{#options}…{/options}` to leave them out. `article_before_citation`: the Word template's own
 * words put "your", "the" or the like right before the award wording, so the wording mustn't
 * start with one (APP-130).
 */
export const LetterTemplateNote = z.enum(['options_on_cash_only', 'article_before_citation']);
export type LetterTemplateNote = z.infer<typeof LetterTemplateNote>;

/**
 * APP-130 D4: one named check of a template's sample. `id` is stable; `name` and `detail` are plain
 * words from the server. `warning`: worth a look, doesn't block. `not_run`: an earlier check failed,
 * so this one couldn't run. A failed check blocks Approve and use; only Send back is offered.
 * `signature` (D98, the seventh) is never failed: its warning is approved only with the approver's
 * own confirmation that the signature is the signer's (`signatureConfirmed`).
 */
export const LetterSampleCheckId = z.enum([
  'fills',
  'options_rows',
  'signed_page',
  'fonts',
  'details_page',
  'doubled_words',
  'sample_mark',
  'signature',
]);
export type LetterSampleCheckId = z.infer<typeof LetterSampleCheckId>;
export const LetterSampleCheck = z.object({
  id: LetterSampleCheckId,
  name: z.string(),
  result: z.enum(['passed', 'failed', 'warning', 'not_run']),
  /** What went wrong or what to look at; null when it passed (a matching signature says so). */
  detail: z.string().nullable(),
  /** D98: who confirmed a signature warning when they approved the letter, and when. */
  confirmedBy: PersonRef.nullable().optional(),
  confirmedAt: Timestamp.nullable().optional(),
  /**
   * APP-205 (SENT-104): a letter pack marked approved confirmed it, when no person did: its hash,
   * and who imported or applied it ("MyRewards setup" from the deployment), never its label.
   */
  confirmedByPack: z.object({ sha256: z.string(), by: z.string() }).nullable().optional(),
  /**
   * SENT-031: a signature warning still to confirm carries an opaque key for it. Approval sends it
   * back as `signatureConfirmed.key`, so the tick holds only for the warning the approver saw.
   */
  key: z.string().optional(),
});
export type LetterSampleCheck = z.infer<typeof LetterSampleCheck>;

export const LetterTemplateView = z.object({
  id: z.uuid(),
  awardType: LetterLibraryType,
  version: z.number().int().positive(),
  name: z.string(),
  format: LetterTemplateFormat,
  fileName: z.string(),
  sizeBytes: z.number().int().nonnegative(),
  signer: PersonRef,
  signerRole: LetterSignerRole,
  /** Template tag or form-field name → merge field. */
  fieldMap: z.record(z.string(), z.string()),
  /** The merge fields the template uses, from the check at upload. */
  fieldsUsed: z.array(z.string()),
  /** What to know before using it, from the check at upload. */
  notes: z.array(LetterTemplateNote),
  scanStatus: ScanStatus,
  status: LetterTemplateStatus,
  uploadedBy: PersonRef.nullable(),
  uploadedAt: Timestamp,
  activatedBy: PersonRef.nullable(),
  activatedAt: Timestamp.nullable(),
  retiredBy: PersonRef.nullable(),
  retiredAt: Timestamp.nullable(),
  retireReason: z.string().nullable(),
  layout: LetterLayout.optional(),
  variant: LetterVariant.optional(),
  approvedAt: Timestamp.nullable().optional(),
  previewSha256: z.string().nullable().optional(),
  requiredFonts: z.array(z.string()).optional(),
  mapped: z.boolean().optional(),
  canDelete: z.boolean().optional(),
  /** APP-130 D1 (QA-066): the number people see, 1, 2, 3… per award type × signer × mix. */
  designVersion: z.number().int().positive().optional(),
  /**
   * The current sample's named checks (D4). Null when there's no sample yet, or the sample was made
   * before checks were stored (D23): a draft like that needs its sample made again to be approved.
   */
  checks: z.array(LetterSampleCheck).nullable().optional(),
  /** Sent back for a fix (D5): who, when and why. */
  sentBack: z
    .object({ at: Timestamp, by: PersonRef.nullable(), reason: z.string() })
    .nullable()
    .optional(),
});
export type LetterTemplateView = z.infer<typeof LetterTemplateView>;

/** A merge field a template may use (for the mapping UI). */
export const LetterField = z.object({
  name: z.string(),
  label: z.string(),
  formats: z.array(LetterTemplateFormat),
  awardTypes: z.array(OperationalAwardTypeCode),
  kind: z.enum(['text', 'loop']),
  columns: z.array(z.string()).optional(),
});
export type LetterField = z.infer<typeof LetterField>;

export const LetterTemplateList = z.object({
  templates: z.array(LetterTemplateView),
  fields: z.array(LetterField),
  /** Whether this person may upload, choose the default and retire templates. */
  canEdit: z.boolean(),
  /** Current administration scope; absent only for older compatible clients/fixtures. */
  editableTypes: z.array(LetterLibraryType).optional(),
});
export type LetterTemplateList = z.infer<typeof LetterTemplateList>;

export const LetterTemplatesQuery = z
  .object({
    awardType: LetterLibraryType.optional(),
    includeRetired: z
      .enum(['true', 'false'])
      .transform((v) => v === 'true')
      .optional(),
  })
  .strict();
export type LetterTemplatesQuery = z.infer<typeof LetterTemplatesQuery>;

/** Text fields of the multipart template upload (the file travels as the `file` part). */
export const UploadLetterTemplateFields = z
  .object({
    awardType: LetterLibraryType,
    name: z.string().trim().min(1).max(120),
    signerRole: LetterSignerRole,
    signerId: z.uuid(),
    /** JSON object of template tag → merge field, for templates whose tags have other names. */
    fieldMap: z.string().max(4000).optional(),
    layout: LetterLayout.optional(),
    variant: LetterVariant.optional(),
  })
  .strict();
export type UploadLetterTemplateFields = z.infer<typeof UploadLetterTemplateFields>;

export const LetterTemplateResponse = z.object({ template: LetterTemplateView });
export type LetterTemplateResponse = z.infer<typeof LetterTemplateResponse>;

export const RetireLetterTemplate = z
  .object({ reason: z.string().trim().min(1).max(500) })
  .strict();
export type RetireLetterTemplate = z.infer<typeof RetireLetterTemplate>;

export const LetterTemplateParams = z.object({ id: z.uuid() });

/** Why a person's letter can't be made or released yet; `field` names the fact to fix. */
export const LetterIssue = z.object({
  code: z.string(),
  field: z.string().nullable(),
});
export type LetterIssue = z.infer<typeof LetterIssue>;

/**
 * `missing`: not generated yet. `checking`: generated, waiting for the virus check. `ready`: can be
 * released. `stale`: something on the award changed since it was made; generate it again.
 * `blocked`: the virus check refused the file; generate it again. `released`: sent.
 */
export const LetterLineStatus = z.enum([
  'missing',
  'checking',
  'ready',
  'stale',
  'blocked',
  'released',
]);
export type LetterLineStatus = z.infer<typeof LetterLineStatus>;

export const LetterRecipientSource = z.enum(['line', 'award', 'default']);

/** Why someone is offered as a letter holder (APP-057's choices, worked out on the server). */
export const LetterHolderRole = z.enum([
  'employee',
  'requester',
  'manager',
  'sector_lead',
  'delegate',
]);
export type LetterHolderRole = z.infer<typeof LetterHolderRole>;

/**
 * One person a letter can go to at release (APP-067): the current holder, the employee, the
 * requester, the employee's supervisor, and for a sector award its Sector Lead and live delegates.
 * Active people only. "Someone else" is anyone active, found through the people search.
 */
export const LetterHolderChoice = PersonRef.extend({
  /** Every reason they're offered, in order; empty for a holder picked by name. */
  roles: z.array(LetterHolderRole),
  /** They hold the letter now. */
  current: z.boolean(),
});
export type LetterHolderChoice = z.infer<typeof LetterHolderChoice>;

export const LetterTemplateRef = z.object({
  variant: LetterVariant.optional(),
  signer: PersonRef.optional(),
  id: z.uuid(),
  version: z.number().int().positive(),
  /** APP-130 D2: the number people see, per award type × signer × mix; `version` is internal. */
  designVersion: z.number().int().positive().optional(),
  name: z.string(),
  format: LetterTemplateFormat,
  status: LetterTemplateStatus,
  /** What to know before using it, from the check at upload. */
  notes: z.array(LetterTemplateNote),
});
export type LetterTemplateRef = z.infer<typeof LetterTemplateRef>;

/**
 * APP-130: where one person's letter stands, as people see it. `waiting`: no letter resolves for
 * their signer and amounts yet (the gap card offers the fixes). `ready_to_read`: made, current and
 * through its virus check, not reviewed yet. `reviewed`: someone read it and it looks right (or a
 * Something's wrong was cleared with a reason). `something_wrong`: it can't be released until it's
 * made again or cleared. The rest are the letter's own status.
 */
export const LetterPersonState = z.enum([
  'waiting',
  'missing',
  'checking',
  'ready_to_read',
  'reviewed',
  'something_wrong',
  'stale',
  'blocked',
  'released',
]);
export type LetterPersonState = z.infer<typeof LetterPersonState>;

/** One verdict on one made letter (APP-130 D10/D11). A letter made again starts unreviewed. */
export const LetterReviewVerdict = z.enum(['looks_right', 'something_wrong', 'cleared']);
export type LetterReviewVerdict = z.infer<typeof LetterReviewVerdict>;
export const LetterReview = z.object({
  verdict: LetterReviewVerdict,
  /** What's wrong, or why it was cleared; optional for Looks right. */
  note: z.string().nullable(),
  by: PersonRef,
  at: Timestamp,
  /** For a clearing: the Something's wrong it answered. */
  answers: z.object({ note: z.string().nullable(), by: PersonRef, at: Timestamp }).nullable(),
});
export type LetterReview = z.infer<typeof LetterReview>;

/** The approved letter standing in for a gap (APP-130 D7/D8), saved on each line in it. */
export const LetterFallback = z.object({
  template: LetterTemplateRef,
  reason: z.string(),
  at: Timestamp,
  by: PersonRef,
  /** It can still be used: approved, through its virus check, not retired, its signer active. */
  usable: z.boolean(),
});
export type LetterFallback = z.infer<typeof LetterFallback>;

/** "Check it against the award": one fact as the letter prints it and as the award has it now. */
export const LetterCheckRow = z.object({
  field: z.string(),
  label: z.string(),
  letter: z.string().nullable(),
  award: z.string().nullable(),
  differs: z.boolean(),
});
export type LetterCheckRow = z.infer<typeof LetterCheckRow>;

/**
 * APP-130: one gap on an award, a signer × mix no letter resolves for (`letterCellKey`). Its card
 * offers an upload into that cell, or another approved letter of the same type and mix, any
 * signer, with a reason (D8).
 */
/** APP-212: MyRewards' own sample letter for the award type, by the version of its file. */
export const BuiltInSampleRef = z.object({ version: z.number().int().positive() });
export type BuiltInSampleRef = z.infer<typeof BuiltInSampleRef>;

export const LetterGap = z.object({
  cellKey: z.string(),
  awardType: LetterLibraryType,
  /** The same three mixes as the coverage grid's cells and the entry step's letter section. */
  mix: LetterMix,
  signer: PersonRef,
  people: z.array(z.object({ lineId: z.uuid(), name: z.string() })),
  fallback: LetterFallback.nullable(),
  /** Approved letters of the same type and mix, any signer, that can stand in. */
  candidates: z.array(LetterTemplateRef),
  /**
   * APP-212: with no stand-in in use, the gap's letters are made from the built-in sample letter
   * meanwhile; null when a stand-in is used. Optional: older servers omit it.
   */
  builtInSample: BuiltInSampleRef.nullable().optional(),
});
export type LetterGap = z.infer<typeof LetterGap>;

export const AwardLetterLine = z.object({
  selectedTemplate: LetterTemplateRef.nullable().optional(),
  publicCitation: z.string().nullable().optional(),
  /**
   * APP-237: making this person's letter needs a reviewed recipient citation: a direct committee
   * Spot award, or a signed-letter template. Optional for older servers.
   */
  citationRequired: z.boolean().optional(),
  lineUpdatedAt: Timestamp.optional(),
  lineId: z.uuid(),
  person: PersonRef,
  cashCents: Cents,
  options: z.number().int().nonnegative(),
  status: LetterLineStatus,
  letter: z
    .object({
      documentId: z.uuid(),
      fileName: z.string(),
      sizeBytes: z.number().int().nonnegative(),
      scanStatus: ScanStatus,
      generatedAt: Timestamp,
      /** Who made it; null when MyRewards made it on its own (APP-244) or a system step did. */
      generatedBy: PersonRef.nullable(),
      /** APP-244: MyRewards made it on its own when the award reached its letters step. */
      automatic: z.boolean().optional(),
      /** The library version it was made from; null when it was made from the built-in sample. */
      template: LetterTemplateRef.nullable(),
      /** APP-212: the built-in sample letter it was made from ("built-in sample v1"), or null. */
      builtInSample: BuiltInSampleRef.nullable().optional(),
    })
    .nullable(),
  /** The facts that changed since the letter was made (for example `employeeNumber`). */
  staleFields: z.array(z.string()),
  /** What must be fixed before this person's letter can be made. */
  issues: z.array(LetterIssue),
  /**
   * APP-246: what to know about this person's letter that never stops it being made or released:
   * `employee_number_missing` (the directory has no employee number, so the letter prints none
   * and its details page leaves the row off). Optional: older servers omit it.
   */
  notes: z.array(LetterIssue).optional(),
  /** The values filled into the letter ("Merge fields used"), when they can be worked out. */
  mergeFields: z.record(z.string(), z.string()).nullable(),
  /** Who the letter goes to: chosen for this person, for the whole award, or the type's default. */
  recipient: PersonRef.extend({ source: LetterRecipientSource }).nullable(),
  /** Who the letter can go to at release, the current holder first (APP-067). */
  holderChoices: z.array(LetterHolderChoice),
  releasedAt: Timestamp.nullable(),
  /** APP-130: where this person's letter stands, in the words people see. */
  state: LetterPersonState.optional(),
  /** The latest verdict on the current letter; null until someone reviews it. */
  review: LetterReview.nullable().optional(),
  /** The gap this person's letter waits on, or stands in for (`letterCellKey`). */
  gap: z.string().nullable().optional(),
  /** The fallback saved for this person's gap, if one was chosen. */
  fallback: LetterFallback.nullable().optional(),
  /**
   * APP-212: no real letter fits this person's signer and mix, so their letter is (or will be) made
   * from the built-in sample letter, marked as a sample; null otherwise.
   */
  builtInSample: BuiltInSampleRef.nullable().optional(),
  /**
   * How the current letter was chosen: the mapping, the gap's fallback, a one-off override, or the
   * built-in sample letter (APP-212).
   */
  madeFrom: z.enum(['mapping', 'fallback', 'override', 'built_in_sample']).nullable().optional(),
  /** D9: made from the fallback, and the letter it stood in for is ready now. */
  makeAgain: z.boolean().optional(),
  /** "Check it against the award": the made letter's facts beside the award's now. */
  check: z.array(LetterCheckRow).optional(),
  /** It can go out in the next release: current, through its checks, nothing to fix. */
  releasable: z.boolean().optional(),
});
export type AwardLetterLine = z.infer<typeof AwardLetterLine>;

/**
 * APP-242: a Spot colleague's letter preview, for the committee, their manager and the Stock Admin
 * team (never the nominator): the real letter, drawn now and marked PREVIEW (`href`), with what it
 * was drawn from. Before the award is made the amount and date are the best known (`amountFrom`,
 * `dateFrom`); after, the award's own.
 */
export const SpotLetterPreview = z.object({
  nominationId: z.uuid(),
  personId: z.uuid(),
  /** It can be drawn now; when it can't, `issues` says what it waits on. */
  available: z.boolean(),
  issues: z.array(LetterIssue),
  /** The title the letter prints as its wording; null while there's none. */
  title: z.string().nullable(),
  cashCents: Cents,
  /**
   * Where the amount comes from: the award once made, else the committee's recommendation, the
   * nominator's suggestion, or the lowest amount the nomination's policy allows.
   */
  amountFrom: z.enum(['award', 'recommendation', 'suggestion', 'policy_minimum']),
  awardDate: PlainDate,
  /** The award's date once made, else the committee's, else today. */
  dateFrom: z.enum(['award', 'committee', 'today']),
  /** The PDF, marked PREVIEW, to frame in the app or open; null when it can't be drawn. */
  href: z.string().nullable(),
});
export type SpotLetterPreview = z.infer<typeof SpotLetterPreview>;

/**
 * APP-242: `GET /api/v1/spot/:id/nominees/:personId/letter-preview` answers `SpotLetterPreview`;
 * `…/letter-preview/file` is the PDF itself, inline, for a frame or a new tab. Both 404 for anyone
 * who may not read it (the nominator, a colleague on it, sample-roles Preview).
 */
export const SpotLetterPreviewParams = z.object({ id: z.uuid(), personId: z.uuid() });
export type SpotLetterPreviewParams = z.infer<typeof SpotLetterPreviewParams>;

/** APP-244: one thing an automatic letter run met: whose letter (or the whole award's), and what. */
export const LetterAutoIssue = z.object({
  lineId: z.uuid().nullable(),
  code: z.string(),
  field: z.string().nullable(),
  /** What it says beyond its code: a doubled word's words (APP-130 D22). */
  message: z.string().optional(),
});
export type LetterAutoIssue = z.infer<typeof LetterAutoIssue>;

/**
 * APP-244: MyRewards making an award's letters on its own when it reached its letters step.
 * `waiting`: being made now. `made`: made, and `issues` names anyone's letter held back. `not_made`:
 * nothing was made, and `issues` says why.
 */
export const LettersAutomatic = z.object({
  status: z.enum(['waiting', 'made', 'not_made']),
  /** When it was asked for (waiting), else when it finished. */
  at: Timestamp,
  issues: z.array(LetterAutoIssue),
});
export type LettersAutomatic = z.infer<typeof LettersAutomatic>;

export const AwardLetters = z.object({
  version: z.number().int().positive().optional(),
  requestId: z.uuid(),
  displayId: z.string().nullable(),
  type: OperationalAwardTypeCode,
  awardName: z.string(),
  stage: StageId.nullable(),
  awardDate: PlainDate.nullable(),
  /**
   * APP-201: the award's letter wording, one for everyone on it (not on a Spot award, whose
   * citation is each recipient's). `ownWording` counts the people whose own wording, saved before
   * APP-201, their letters still print; a change for the award replaces it.
   */
  wording: AwardLetterWording.extend({ ownWording: z.number().int().nonnegative() })
    .nullable()
    .optional(),
  /** The default template for this award type and the others that can be picked. */
  templates: z.object({
    default: LetterTemplateRef.nullable(),
    available: z.array(LetterTemplateRef),
  }),
  defaultRecipient: z.enum(['requester', 'manager', 'employee']),
  lines: z.array(AwardLetterLine),
  /** Every letter still to go out is made, checked and current. */
  ready: z.boolean(),
  /** Every person's letter is released. */
  released: z.boolean(),
  actions: z.object({
    generate: z.boolean(),
    /** Some letters can go out now (APP-130: release the ready ones). */
    release: z.boolean(),
    overrideTemplate: z.boolean().optional(),
    /** Review a letter: Looks right, Something's wrong, or clear one. */
    review: z.boolean().optional(),
    /** Choose another approved letter for a gap (Stock Admin, System Admin). */
    fallback: z.boolean().optional(),
    /** APP-201: change the award's letter wording (the Stock Admin who holds the claim). */
    wording: z.boolean().optional(),
  }),
  /** APP-130: each signer × mix no letter resolves for, with its fixes. */
  gaps: z.array(LetterGap).optional(),
  /** How many letters: in all, released, ready to go now, read or not, waiting, flagged. */
  counts: z
    .object({
      total: z.number().int().nonnegative(),
      released: z.number().int().nonnegative(),
      releasable: z.number().int().nonnegative(),
      reviewed: z.number().int().nonnegative(),
      notReviewed: z.number().int().nonnegative(),
      waiting: z.number().int().nonnegative(),
      somethingWrong: z.number().int().nonnegative(),
    })
    .optional(),
  /** D14: the first release; the award date is fixed from then on. */
  firstReleasedAt: Timestamp.nullable().optional(),
  /** D16: a letter this award waited on can now be made from its own template. */
  templateArrived: z.boolean().optional(),
  /**
   * APP-244: the automatic run of these letters, when the award's step asked for one and no
   * person's Generate has since spoken for them; null otherwise. Optional: older servers omit it.
   */
  automatic: LettersAutomatic.nullable().optional(),
  /**
   * APP-244, Spot only: how the colleague's letter reaches them once released. `hand_out`: their
   * manager hands it over. `automatic`: it's sent to them straight away, with a copy to the manager
   * (APP-245 delivers it). The manager is the letter's holder either way.
   */
  releasePath: z.enum(['hand_out', 'automatic']).optional(),
  /**
   * Generate only: letters this call held back while it made the others, each with why (D22: a
   * word printed twice beside a merge field, its field and words in `message`). APP-247: also a
   * Spot letter title saved at the check, when its letter couldn't be made again with it; the
   * title stays saved.
   */
  held: z
    .array(
      z.object({
        lineId: z.uuid(),
        code: z.string(),
        field: z.string().nullable(),
        message: z.string(),
      }),
    )
    .optional(),
});
export type AwardLetters = z.infer<typeof AwardLetters>;

/**
 * APP-130 D10/D11: one person's letter, read. `something_wrong` needs a note and stops that letter
 * going out; `clear` answers a Something's wrong with a reason. The document is the letter as read:
 * a letter made again since must be read again.
 */
export const ReviewLetter = z
  .object({
    documentId: z.uuid(),
    verdict: z.enum(['looks_right', 'something_wrong', 'clear']),
    note: z.string().trim().min(1).max(1000).optional(),
  })
  .strict()
  .superRefine((v, ctx) => {
    if (v.verdict !== 'looks_right' && v.note === undefined)
      ctx.addIssue({
        code: 'custom',
        path: ['note'],
        message:
          v.verdict === 'clear' ? 'Say why it can go as it is.' : 'Say what’s wrong with it.',
      });
  });
export type ReviewLetter = z.infer<typeof ReviewLetter>;

/**
 * APP-130 D7/D8: the approved letter to use for one gap (`letterCellKey`) until its own letter is
 * ready, with the reason; saved on each person in it. `null` takes a saved fallback off.
 */
export const SaveLetterFallback = z
  .object({
    cellKey: z.string().min(1).max(200),
    templateId: z.uuid().nullable(),
    reason: z.string().trim().min(1).max(500),
  })
  .strict();
export type SaveLetterFallback = z.infer<typeof SaveLetterFallback>;

export const GenerateLetters = z
  .object({
    version: z.number().int().positive().optional(),
    /** A ready template of this award type; the type's default when omitted. */
    templateId: z.uuid().optional(),
    overrideReason: z.string().trim().min(1).max(500).optional(),
    /** A new award date; every letter is made with it. */
    awardDate: PlainDate.optional(),
    /**
     * Only these people's letters (APP-067): made again, the others left as they are. A new award
     * date is in every letter, so it can't come with only some of them.
     */
    lineIds: z
      .array(z.uuid())
      .min(1)
      .max(500)
      .refine((ids) => new Set(ids).size === ids.length, 'Name each person once.')
      .optional(),
  })
  .strict();
export type GenerateLetters = z.infer<typeof GenerateLetters>;

export const ReleaseLetters = z
  .object({
    version: z.number().int().positive().optional(),
    /** The award date the Stock Admin confirmed; it must be the one the letters were made with. */
    awardDate: PlainDate,
    /** Letter holders chosen on this screen; other people keep theirs or the default. */
    recipients: z
      .array(z.object({ lineId: z.uuid(), recipientId: z.uuid() }).strict())
      .max(500)
      .superRefine((rows, ctx) => {
        const seen = new Set<string>();
        rows.forEach((r, i) => {
          if (seen.has(r.lineId))
            ctx.addIssue({
              code: 'custom',
              path: [i, 'lineId'],
              message: 'Choose one letter holder per person.',
            });
          seen.add(r.lineId);
        });
      })
      .optional(),
    /**
     * Why a holder changed (APP-056): required when a holder chosen here replaces the one a line
     * already had (chosen at entry, for the award, or the type's default). Audited on each line.
     */
    reason: z.string().trim().min(1).max(500).optional(),
    /**
     * APP-130: release only these people's letters now (the ready ones); the rest follow later
     * to the same holder. Omitted: every letter still to go, all of them ready.
     */
    lineIds: z
      .array(z.uuid())
      .min(1)
      .max(500)
      .refine((ids) => new Set(ids).size === ids.length, 'Name each person once.')
      .optional(),
  })
  .strict();
export type ReleaseLetters = z.infer<typeof ReleaseLetters>;

export const AwardLettersParams = z.object({ requestId: z.uuid() });
export const AwardLetterParams = z.object({ requestId: z.uuid(), lineId: z.uuid() });

/** Committee routing when the saved supervisor can no longer hand out the letter. */
export const RerouteSpotLetter = z
  .object({
    version: z.number().int().positive(),
    lineUpdatedAt: Timestamp,
    supervisorId: z.uuid(),
    reason: z.string().trim().min(1).max(2000),
  })
  .strict();
export type RerouteSpotLetter = z.infer<typeof RerouteSpotLetter>;

/** Deliberately prepared for the recipient; private nomination text is never an input. */
export const SaveSpotCitation = z
  .object({
    version: z.number().int().positive(),
    lineUpdatedAt: Timestamp,
    publicCitation: LetterWordingText.pipe(z.string().min(1)),
    reason: z.string().trim().min(1).max(2000),
  })
  .strict();
export type SaveSpotCitation = z.infer<typeof SaveSpotCitation>;

/**
 * APP-201: the Stock Admin team changes the award's letter wording, once for everyone on it. Null
 * goes back to the default (the award's name, else the program's name).
 */
export const SaveLetterWording = z
  .object({
    version: z.number().int().positive(),
    letterWording: LetterWordingText.pipe(z.string().min(1)).nullable(),
    reason: z.string().trim().min(1).max(2000),
  })
  .strict();
export type SaveLetterWording = z.infer<typeof SaveLetterWording>;

/** APP-116: bindings use immutable approved versions; policies resolve the actual signer. */
export const LetterSigningPolicy = z
  .object({
    awardType: LetterLibraryType,
    source: LetterSignerSource,
    personId: z.uuid().nullable(),
  })
  .strict()
  .superRefine((p, ctx) => {
    if ((p.source === 'person') !== (p.personId !== null))
      ctx.addIssue({
        code: 'custom',
        path: ['personId'],
        message: 'Choose a person only for a named-individual policy.',
      });
  });
export const LetterMappingConfig = z.object({
  revision: z.number().int().nonnegative(),
  ceoPersonId: z.uuid().nullable(),
  ceoPerson: PersonRef.nullable().optional(),
  sectors: z.array(z.object({ id: z.string(), name: z.string() })).optional(),
  policyPeople: z.array(PersonRef).optional(),
  policies: z.array(LetterSigningPolicy),
  templateIds: z.array(z.uuid()),
  canEdit: z.boolean(),
  canEditSpot: z.boolean(),
});
export type LetterMappingConfig = z.infer<typeof LetterMappingConfig>;
export const SaveLetterMappings = LetterMappingConfig.omit({
  canEdit: true,
  canEditSpot: true,
  ceoPerson: true,
  sectors: true,
  policyPeople: true,
})
  .extend({ reason: z.string().trim().min(1).max(500) })
  .strict()
  .superRefine((v, ctx) => {
    if (v.policies.length !== 7 || new Set(v.policies.map((p) => p.awardType)).size !== 7)
      ctx.addIssue({
        code: 'custom',
        path: ['policies'],
        message: 'Define one policy for every award type.',
      });
    if (new Set(v.templateIds).size !== v.templateIds.length)
      ctx.addIssue({
        code: 'custom',
        path: ['templateIds'],
        message: 'Choose each template once.',
      });
  });
export type SaveLetterMappings = z.infer<typeof SaveLetterMappings>;
export const TestLetterMapping = z
  .object({
    awardType: LetterLibraryType,
    variant: LetterVariant.exclude(['any']),
    giverId: z.uuid().nullable(),
    sectorId: z.string().max(100).nullable(),
  })
  .strict();
export type TestLetterMapping = z.infer<typeof TestLetterMapping>;
export const LetterMappingTestResult = z.object({
  signer: PersonRef.nullable(),
  source: LetterSignerSource.nullable(),
  template: LetterTemplateView.nullable(),
  issues: z.array(z.string()),
});
export type LetterMappingTestResult = z.infer<typeof LetterMappingTestResult>;
/** SENT-031: an approver's tick, tied to the signature warning they saw by its key. */
export const SignatureConfirmation = z.object({ key: z.string().min(1).max(128) }).strict();
export type SignatureConfirmation = z.infer<typeof SignatureConfirmation>;
export const ApproveLetterTemplate = z
  .object({
    previewSha256: z.string().regex(/^[a-f0-9]{64}$/),
    reason: z.string().trim().min(1).max(500),
    /**
     * APP-130 D98/D106, SENT-031: the approver confirms the signature in the file is the signer's.
     * Needed when the signature check is a warning, with the key of the warning they saw (from the
     * template or coverage view); the server records who, when and the key.
     */
    signatureConfirmed: SignatureConfirmation.optional(),
  })
  .strict();
export type ApproveLetterTemplate = z.infer<typeof ApproveLetterTemplate>;
export const LetterSample = z.object({ sha256: z.string(), url: z.string() });
export type LetterSample = z.infer<typeof LetterSample>;
